import { inject, Injectable } from "@angular/core";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { MonitoringDirectBomEntry } from "@domain/project/direct-bom/monitoring-direct-bom-entry";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { getMonitoringBomServiceDefinition, MonitoringBomServiceId } from "@domain/project/monitoring-bom-service-definition";
import { MonitoringProductData } from "@domain/project/monitoring-product-data/monitoring-product-data";
import { MonitoringProductDataService } from "@domain/project/monitoring-product-data/monitoring-product-data.service";
import { MonitoringCategorySection } from "@domain/project/monitoring-product-list/monitoring-category-section";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";
import { MonitoringGroupSection } from "@domain/project/monitoring-product-list/monitoring-group-section";
import { MonitoringProject } from "@domain/project/monitoring-project";
import {
  assignConfigSubPositionIds,
  comparePositionThenName,
  getLatestTopLevelPositionId,
} from "@domain/project/product-list/product-list-position.util";
import { ALPHABET } from "@utils/alphabet";
import { forkJoin, map, Observable, of, tap } from "rxjs";

export interface AdditionalMonitoringEntryConfig {
  id: string;
  name: string;
  mainProductId?: string;
  positionNumber?: string;
  additionalProductListQuantity: number;
  setAdditionalProductListQuantity?(quantity: number): void;
}

export interface AdditionalMonitoringSectionMapping {
  config: AdditionalMonitoringEntryConfig;
  data: MonitoringProductData[];
}

export interface AdditionalMonitoringConfigSection {
  config: AdditionalMonitoringEntryConfig;
  productSection: {
    category: string;
    mappings: AdditionalMonitoringSectionMapping[];
  };
  isDirectBomEntry?: boolean;
}

export interface MonitoringBomServiceRow {
  id: MonitoringBomServiceId;
  position: number;
  name: string;
  partNumber: string;
  notes: string;
}

@Injectable({ providedIn: "root" })
export class MonitoringProductListService {
  private monitoringProductDataService = inject(MonitoringProductDataService);

  private readonly UNGROUPED_PRODUCTS_TITLE: string = $localize`:@@productList.defaultGroup:Sonstige Produkte`;

  createGroupSections(project: MonitoringProject, floorplans?: Floorplan[]): Observable<MonitoringGroupSection[]> {
    const targetFloorplans = floorplans ?? project.cachedFloorplans;
    const ungroupedSections$: Observable<MonitoringGroupSection>[] = targetFloorplans.map((floorplan) => {
      const ungroupedGroup = this.resolveUngroupedProducts(floorplan, project);
      return this.createGroupSection(ungroupedGroup, floorplan);
    });

    const explicitGroupSections$: Observable<MonitoringGroupSection>[] = project.groups
      .filter((group) => group.references.some((r) => targetFloorplans.some((f) => f.id === r.floorplanId)))
      .map((group) => {
        const floorplan = targetFloorplans.find((f) => group.references.some((r) => r.floorplanId === f.id));
        return this.createGroupSection(group, floorplan, group.name);
      });

    const floorplanName = floorplans?.length === 1 ? floorplans[0].name : undefined;
    const mergedUngrouped$: Observable<MonitoringGroupSection[]> =
      ungroupedSections$.length === 0
        ? of([])
        : forkJoin(ungroupedSections$).pipe(map((sections) => [this.mergeUngroupedMonitoringSections(sections, floorplanName)]));

    const explicitGroups$: Observable<MonitoringGroupSection[]> =
      explicitGroupSections$.length === 0 ? of([]) : forkJoin(explicitGroupSections$);

    return forkJoin([mergedUngrouped$, explicitGroups$]).pipe(map(([ungrouped, explicit]) => [...ungrouped, ...explicit]));
  }

  private mergeUngroupedMonitoringSections(sections: MonitoringGroupSection[], floorplanName?: string): MonitoringGroupSection {
    const merged = new MonitoringGroupSection(this.UNGROUPED_PRODUCTS_TITLE, floorplanName);
    merged.patientMonitors = new MonitoringCategorySection(
      sections.flatMap((s) => s.patientMonitors?.devices ?? []),
      sections.flatMap((s) => s.patientMonitors?.placeholders ?? []),
      sections.flatMap((s) => s.patientMonitors?.mappings ?? []),
      MonitoringCategory.PATIENT_MONITORS,
    );
    merged.centralStations = new MonitoringCategorySection(
      sections.flatMap((s) => s.centralStations?.devices ?? []),
      sections.flatMap((s) => s.centralStations?.placeholders ?? []),
      sections.flatMap((s) => s.centralStations?.mappings ?? []),
      MonitoringCategory.CENTRAL_STATIONS,
    );
    merged.telemetries = new MonitoringCategorySection(
      sections.flatMap((s) => s.telemetries?.devices ?? []),
      sections.flatMap((s) => s.telemetries?.placeholders ?? []),
      sections.flatMap((s) => s.telemetries?.mappings ?? []),
      MonitoringCategory.TELEMETRY,
    );
    merged.kvms = new MonitoringCategorySection(
      sections.flatMap((s) => s.kvms?.devices ?? []),
      sections.flatMap((s) => s.kvms?.placeholders ?? []),
      sections.flatMap((s) => s.kvms?.mappings ?? []),
      MonitoringCategory.KVMS,
    );
    merged.displays = new MonitoringCategorySection(
      sections.flatMap((s) => s.displays?.devices ?? []),
      sections.flatMap((s) => s.displays?.placeholders ?? []),
      sections.flatMap((s) => s.displays?.mappings ?? []),
      MonitoringCategory.DISPLAYS,
    );
    merged.utilities = new MonitoringCategorySection(
      sections.flatMap((s) => s.utilities?.devices ?? []),
      sections.flatMap((s) => s.utilities?.placeholders ?? []),
      sections.flatMap((s) => s.utilities?.mappings ?? []),
      MonitoringCategory.UTILITY,
    );
    return merged;
  }

  private resolveUngroupedProducts(floorplan: Floorplan, project: MonitoringProject): ProductItemGroup {
    const groupedIds = new Set(
      project.groups.flatMap((g) =>
        g.references.filter((r) => r.floorplanId === floorplan.id).map((r) => r.floorplanProductItemId),
      ),
    );
    const ungroupedGroup = new ProductItemGroup(this.UNGROUPED_PRODUCTS_TITLE, 0, [floorplan.id]);
    const ungroupedRefs = [
      ...floorplan.patientMonitors.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.patientMonitorPlaceholders.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.centralStations.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.centralStationPlaceholders.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.telemetries.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.telemetryPlaceholders.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.kvms.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.kvmPlaceholders.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.displays.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.displayPlaceholders.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.utilities.filter((d) => !groupedIds.has(d.id)),
      ...floorplan.utilityPlaceholders.filter((d) => !groupedIds.has(d.id)),
    ].map((item) => new FloorplanItemReference(floorplan.id, item.id, this.resolveMonitoringKind(floorplan, item.id)));
    ungroupedGroup.addReferences(ungroupedRefs);
    return ungroupedGroup;
  }

  private resolveMonitoringKind(floorplan: Floorplan, itemId: string): MonitoringCategory {
    if (floorplan.patientMonitors.some((d) => d.id === itemId)) return MonitoringCategory.PATIENT_MONITORS;
    if (floorplan.patientMonitorPlaceholders.some((d) => d.id === itemId)) return MonitoringCategory.PATIENT_MONITORS;
    if (floorplan.centralStations.some((d) => d.id === itemId)) return MonitoringCategory.CENTRAL_STATIONS;
    if (floorplan.centralStationPlaceholders.some((d) => d.id === itemId)) return MonitoringCategory.CENTRAL_STATIONS;
    if (floorplan.telemetries.some((d) => d.id === itemId)) return MonitoringCategory.TELEMETRY;
    if (floorplan.telemetryPlaceholders.some((d) => d.id === itemId)) return MonitoringCategory.TELEMETRY;
    if (floorplan.kvms.some((d) => d.id === itemId)) return MonitoringCategory.KVMS;
    if (floorplan.kvmPlaceholders.some((d) => d.id === itemId)) return MonitoringCategory.KVMS;
    if (floorplan.displays.some((d) => d.id === itemId)) return MonitoringCategory.DISPLAYS;
    if (floorplan.displayPlaceholders.some((d) => d.id === itemId)) return MonitoringCategory.DISPLAYS;
    return MonitoringCategory.UTILITY;
  }

  createGroupSection(group: ProductItemGroup, floorplan?: Floorplan, floorplanName?: string): Observable<MonitoringGroupSection> {
    const groupSection = new MonitoringGroupSection(group.name, floorplanName);
    return this.monitoringProductDataService.collectGroupData(group, floorplan!).pipe(
      tap((results) => {
        const patientMonitors: FloorplanPatientMonitor[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.PATIENT_MONITORS)
              .map((r) => floorplan.patientMonitors.find((pm) => pm.id === r.floorplanProductItemId))
              .filter((pm): pm is FloorplanPatientMonitor => !!pm)
          : [];
        const patientMonitorPlaceholders: FloorplanSimplePlaceholder[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.PATIENT_MONITORS)
              .map((r) => floorplan.patientMonitorPlaceholders.find((p) => p.id === r.floorplanProductItemId))
              .filter((p): p is FloorplanSimplePlaceholder => !!p)
          : [];

        const centralStations: FloorplanCentralStation[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.CENTRAL_STATIONS)
              .map((r) => floorplan.centralStations.find((cs) => cs.id === r.floorplanProductItemId))
              .filter((cs): cs is FloorplanCentralStation => !!cs)
          : [];
        const centralStationPlaceholders: FloorplanSimplePlaceholder[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.CENTRAL_STATIONS)
              .map((r) => floorplan.centralStationPlaceholders.find((p) => p.id === r.floorplanProductItemId))
              .filter((p): p is FloorplanSimplePlaceholder => !!p)
          : [];

        const telemetries: FloorplanTelemetry[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.TELEMETRY)
              .map((r) => floorplan.telemetries.find((t) => t.id === r.floorplanProductItemId))
              .filter((t): t is FloorplanTelemetry => !!t)
          : [];
        const telemetryPlaceholders: FloorplanSimplePlaceholder[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.TELEMETRY)
              .map((r) => floorplan.telemetryPlaceholders.find((p) => p.id === r.floorplanProductItemId))
              .filter((p): p is FloorplanSimplePlaceholder => !!p)
          : [];

        const kvms: FloorplanKvm[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.KVMS)
              .map((r) => floorplan.kvms.find((kvm) => kvm.id === r.floorplanProductItemId))
              .filter((kvm): kvm is FloorplanKvm => !!kvm)
          : [];
        const kvmPlaceholders: FloorplanSimplePlaceholder[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.KVMS)
              .map((r) => floorplan.kvmPlaceholders.find((p) => p.id === r.floorplanProductItemId))
              .filter((p): p is FloorplanSimplePlaceholder => !!p)
          : [];

        const displays: FloorplanDisplay[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.DISPLAYS)
              .map((r) => floorplan.displays.find((d) => d.id === r.floorplanProductItemId))
              .filter((d): d is FloorplanDisplay => !!d)
          : [];
        const displayPlaceholders: FloorplanSimplePlaceholder[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.DISPLAYS)
              .map((r) => floorplan.displayPlaceholders.find((p) => p.id === r.floorplanProductItemId))
              .filter((p): p is FloorplanSimplePlaceholder => !!p)
          : [];

        const utilities: FloorplanUtility[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.UTILITY)
              .map((r) => floorplan.utilities.find((u) => u.id === r.floorplanProductItemId))
              .filter((u): u is FloorplanUtility => !!u)
          : [];
        const utilityPlaceholders: FloorplanSimplePlaceholder[] = floorplan
          ? group.references
              .filter((r) => r.floorplanId === floorplan.id && r.kind === MonitoringCategory.UTILITY)
              .map((r) => floorplan.utilityPlaceholders.find((p) => p.id === r.floorplanProductItemId))
              .filter((p): p is FloorplanSimplePlaceholder => !!p)
          : [];

        groupSection.createPatientMonitors(patientMonitors, patientMonitorPlaceholders, results[0]);
        groupSection.createCentralStations(centralStations, centralStationPlaceholders, results[1]);
        groupSection.createTelemetries(telemetries, telemetryPlaceholders, results[2]);
        groupSection.createKvms(kvms, kvmPlaceholders, results[3]);
        groupSection.createDisplays(displays, displayPlaceholders, results[4]);
        groupSection.createUtilities(utilities, utilityPlaceholders, results[5]);
      }),
      map(() => groupSection),
    );
  }

  getLatestPositionIdForProducts(
    groupSections: MonitoringGroupSection[],
    additionalSections: AdditionalMonitoringConfigSection[] = [],
  ) {
    return getLatestTopLevelPositionId(this.collectPositionIds(groupSections, additionalSections));
  }

  createServiceRows(
    project: MonitoringProject,
    groupSections: MonitoringGroupSection[],
    additionalSections: AdditionalMonitoringConfigSection[] = [],
  ): MonitoringBomServiceRow[] {
    const latestPositionId = this.getLatestPositionIdForProducts(groupSections, additionalSections);
    return project.monitoringBomServices
      .filter((service) => service.required)
      .map((service, index) => {
        const definition = getMonitoringBomServiceDefinition(service.id);
        return {
          id: service.id,
          position: latestPositionId + index + 1,
          name: definition.label(),
          partNumber: definition.partNumber,
          notes: service.notes,
        };
      });
  }

  createAdditionalConfigSections(project: MonitoringProject): Observable<AdditionalMonitoringConfigSection[]> {
    const directBomEntries = project.directBomEntries.filter((entry) => entry.quantity > 0);
    return forkJoin([
      this.monitoringProductDataService.collectAdditionalConfigData(project),
      this.monitoringProductDataService.collectDirectBomData(directBomEntries),
    ]).pipe(
      map(([[patientMonitors, centralStations, telemetries, kvms, displays, utilities], directEntryData]) =>
        [
          ...patientMonitors.map((mapping) => this.createAdditionalConfigSection(mapping, MonitoringCategory.PATIENT_MONITORS)),
          ...centralStations.map((mapping) => this.createAdditionalConfigSection(mapping, MonitoringCategory.CENTRAL_STATIONS)),
          ...telemetries.map((mapping) => this.createAdditionalConfigSection(mapping, MonitoringCategory.TELEMETRY)),
          ...kvms.map((mapping) => this.createAdditionalConfigSection(mapping, MonitoringCategory.KVMS)),
          ...displays.map((mapping) => this.createAdditionalConfigSection(mapping, MonitoringCategory.DISPLAYS)),
          ...utilities.map((mapping) => this.createAdditionalConfigSection(mapping, MonitoringCategory.UTILITY)),
          ...directBomEntries.map((entry) => this.createDirectBomSection(project, entry, directEntryData.get(entry.id) ?? [])),
        ].sort((a, b) => comparePositionThenName(a.config.positionNumber, a.config.name, b.config.positionNumber, b.config.name)),
      ),
    );
  }

  private collectPositionIds(
    groupSections: MonitoringGroupSection[],
    additionalSections: AdditionalMonitoringConfigSection[] = [],
  ) {
    const positionIds: string[] = [];
    for (const section of groupSections) {
      for (const group of [
        section.patientMonitors,
        section.centralStations,
        section.telemetries,
        section.kvms,
        section.displays,
        section.utilities,
      ]) {
        if (!group) continue;
        group.mappings.forEach((mapping) => {
          positionIds.push(...this.getPositionIdsFromProductData(mapping.data));
        });

        group.devices.forEach((device) => {
          positionIds.push(...this.getPositionIdsFromProductData(device.products));
        });

        group.placeholders.forEach((placeholder) => {
          positionIds.push(...this.getPositionIdsFromProductData(placeholder.products));
        });
      }
    }

    for (const section of additionalSections) {
      section.productSection.mappings.forEach((mapping) => {
        positionIds.push(...this.getPositionIdsFromProductData(mapping.data));
      });
    }

    return positionIds;
  }

  private getPositionIdsFromProductData(productData: (MonitoringProductData | PlaceholderProduct)[] = []) {
    return productData.map((data) => data.positionId ?? "").filter((positionId) => positionId.length > 0);
  }

  private createAdditionalConfigSection(
    mapping: MonitoringConfigProductMap,
    category: MonitoringCategory,
  ): AdditionalMonitoringConfigSection {
    assignConfigSubPositionIds(mapping.data);
    return {
      config: mapping.config,
      productSection: new MonitoringCategorySection([], [], [mapping], category),
    };
  }

  private createDirectBomSection(
    project: MonitoringProject,
    entry: MonitoringDirectBomEntry,
    products: MonitoringProductData[],
  ): AdditionalMonitoringConfigSection {
    const config: AdditionalMonitoringEntryConfig = {
      id: entry.id,
      name: entry.name,
      mainProductId: entry.mainProductId,
      positionNumber: entry.positionNumber,
      additionalProductListQuantity: entry.quantity,
      setAdditionalProductListQuantity: (quantity) => project.setDirectBomEntryQuantity(entry, quantity),
    };
    const data = products.map((product, index) => {
      product.positionId = index === 0 ? entry.positionNumber : `${entry.positionNumber}.${ALPHABET[index - 1] ?? index}`;
      return product;
    });

    return {
      config,
      productSection: {
        category: entry.category,
        mappings: [{ config, data }],
      },
      isDirectBomEntry: true,
    };
  }
}
